import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageSquare, Calendar, User } from 'lucide-react';

const ContactUs = () => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    appointmentType: '',
    preferredDate: '',
    preferredTime: ''
  });

  const [activeTab, setActiveTab] = useState('contact');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formEndpoint = useMemo(() => {
    return process.env.REACT_APP_GETFORM_ENDPOINT || '/api/send-email';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setToast({ show: true, message: 'Name, email, and message are required.', type: 'error' });
      return;
    }
    setIsSubmitting(true);
    const payload = {
      type: activeTab === 'appointment' ? 'Appointment Request' : 'General Inquiry',
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: activeTab === 'contact' ? formData.subject : formData.appointmentType || 'Appointment',
      message: formData.message,
      preferredDate: activeTab === 'appointment' ? formData.preferredDate : undefined,
      preferredTime: activeTab === 'appointment' ? formData.preferredTime : undefined,
      submittedAt: new Date().toISOString(),
    };

    try {
      const resp = await fetch(formEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Form submission failed');
      }
      setToast({ show: true, message: 'Message sent! We will contact you soon.', type: 'success' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        appointmentType: '',
        preferredDate: '',
        preferredTime: ''
      });
    } catch (err) {
      console.error(err);
      setToast({ show: true, message: 'There was an issue sending your message. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-hide toast and prevent overlapping timers
  useEffect(() => {
    if (!toast.show) return;
    const timer = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3600);
    return () => clearTimeout(timer);
  }, [toast.show]);

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      details: ['Kuchaman - Sikar - Didwana Rd', 'Tilak Nagar, Sikar', 'Rajasthan 332001, India'],
      action: () => window.open('https://maps.google.com/?q=J43W+JV5,+Kuchaman+-+Sikar+-+Didwana+Rd,+Tilak+Nagar,+Sikar,+Rajasthan+332001', '_blank')
    },
    {
      icon: Phone,
      title: 'Phone Numbers',
      details: ['Main: +91 77270 48951', 'Emergency: +91 77270 48951', 'Appointments: +91 77270 48951'],
      action: () => window.location.href = 'tel:+917727048951'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@sharmaortho.com', 'appointments@sharmaortho.com', 'emergency@sharmaortho.com'],
      action: () => window.location.href = 'mailto:info@sharmaortho.com'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Monday - Saturday: 9:00 AM - 7:00 PM', 'Sunday: 10:00 AM - 2:00 PM', 'Emergency: 24/7 Available'],
      action: null
    }
  ];

  const appointmentTypes = [
    'General Consultation',
    'Joint Replacement Consultation', 
    'Sports Injury Assessment',
    'Spine Surgery Consultation',
    'Physiotherapy Session',
    'Follow-up Appointment',
    'Emergency Consultation',
    'Second Opinion'
  ];

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM'
  ];

  return (
    <div className="overflow-x-hidden bg-gradient-to-b from-white via-gray-50 to-white">
      {toast.show && (
        <div className="fixed top-6 right-6 z-50 animate-fadeUp">
          <div
            className={`relative px-5 py-4 rounded-2xl shadow-2xl backdrop-blur-2xl border text-sm font-semibold flex items-center gap-3 transition-all ${
              toast.type === 'success'
                ? 'bg-gradient-to-r from-emerald-700/90 via-emerald-600/85 to-emerald-700/90 text-emerald-50 border-emerald-300/50'
                : 'bg-gradient-to-r from-rose-700/90 via-rose-600/85 to-rose-700/90 text-rose-50 border-rose-300/50'
            }`}
            style={{ fontFamily: '"Poppins", "Inter", "Helvetica Neue", sans-serif' }}
          >
            <span
              className={`w-9 h-9 rounded-xl flex items-center justify-center border shadow-inner ${
                toast.type === 'success'
                  ? 'bg-emerald-500/40 border-emerald-200/70'
                  : 'bg-rose-500/40 border-rose-200/70'
              }`}
            >
              {toast.type === 'success' ? '✅' : '⚠️'}
            </span>
            <div className="flex-1 leading-snug">
              <div className="text-[11px] uppercase tracking-[0.12em] opacity-80">
                {toast.type === 'success' ? 'Message Sent' : 'Submission Error'}
              </div>
              <div className="text-base font-semibold">{toast.message}</div>
            </div>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="text-[11px] px-3 py-1.5 rounded-full border border-white/40 hover:bg-white/10 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0f1c3f] via-[#132a63] to-[#0c1a3a] text-white py-20">
        {/* Base overlay for contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-[#0f1c3f]/70 to-[#0a1733]/80 pointer-events-none" />
        {/* Soft glows */}
        <div className="absolute inset-0 opacity-60 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 16% 18%, rgba(99,180,255,0.32), transparent 26%),
                            radial-gradient(circle at 80% 12%, rgba(255,255,255,0.12), transparent 32%),
                            radial-gradient(circle at 70% 82%, rgba(79,140,255,0.20), transparent 34%)`
        }} />
        {/* Subtle grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px),
                            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)`,
          backgroundSize: '160px 160px'
        }} />
        {/* Floating highlights */}
        <div className="absolute -left-10 top-10 w-40 h-40 bg-blue-400/25 blur-3xl animate-floatSlow pointer-events-none" />
        <div className="absolute right-10 bottom-10 w-48 h-48 bg-cyan-300/20 blur-3xl animate-floatSlowDel pointer-events-none" />
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center space-y-6">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/12 border border-white/25 text-sm font-semibold shadow-lg backdrop-blur animate-pulseGlow">
            📞 Reach out to our care team
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight drop-shadow-sm">
            Contact Us
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
            Get in touch for appointments, inquiries, or any questions about our orthopedic services. We’re here to help you on your path to recovery.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm text-blue-50/90">
            <span className="px-3 py-1 rounded-full border border-white/20 bg-white/10">Joint & Spine Specialists</span>
            <span className="px-3 py-1 rounded-full border border-white/20 bg-white/10">24/7 Emergency Support</span>
            <span className="px-3 py-1 rounded-full border border-white/20 bg-white/10">Physio & Rehabilitation</span>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes floatSlow { 0% { transform: translateY(0); } 50% { transform: translateY(-12px); } 100% { transform: translateY(0); } }
        @keyframes floatSlowDel { 0% { transform: translateY(0); } 50% { transform: translateY(10px); } 100% { transform: translateY(0); } }
        @keyframes pulseGlow { 0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.25);} 70% { box-shadow: 0 0 0 12px rgba(255,255,255,0);} 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0);} }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px);} to { opacity: 1; transform: translateY(0);} }
        .animate-floatSlow { animation: floatSlow 14s ease-in-out infinite; }
        .animate-floatSlowDel { animation: floatSlowDel 16s ease-in-out infinite; }
        .animate-pulseGlow { animation: pulseGlow 3s ease-in-out infinite; }
        .animate-fadeUp { animation: fadeUp 0.8s ease-out forwards; }
      `}</style>

      {/* Quick Contact Actions */}
      <section className="py-12 bg-gradient-to-r from-blue-50 via-white to-blue-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Call Now', icon: Phone, href: 'tel:+917727048951', bg: 'from-emerald-500 to-emerald-600', desc: 'Speak with our coordinator' },
              { label: 'WhatsApp', icon: MessageSquare, href: 'https://wa.me/917727048951?text=I would like to book an appointment', bg: 'from-green-500 to-green-600', desc: 'Text us for appointments', blank: true },
              { label: 'Book Appointment', icon: Calendar, action: () => setActiveTab('appointment'), bg: 'from-primary-600 to-primary-700', desc: 'Schedule with specialists' },
              { label: 'Send Email', icon: Mail, href: 'mailto:info@sharmaortho.com', bg: 'from-blue-600 to-blue-700', desc: 'Detailed queries & reports' }
            ].map((btn, idx) => {
              const Icon = btn.icon;
              const Card = (
                <div className="p-5 rounded-2xl bg-white shadow-lg border border-gray-100 hover:shadow-xl transition">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`w-10 h-10 rounded-xl bg-gradient-to-r ${btn.bg} flex items-center justify-center text-white shadow-md`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="font-semibold text-gray-900">{btn.label}</div>
                  </div>
                  <p className="text-sm text-gray-600">{btn.desc}</p>
                </div>
              );
              if (btn.action) {
                return (
                  <button
                    key={idx}
                    onClick={btn.action}
                    className="text-left"
                  >
                    {Card}
                  </button>
                );
              }
              return (
                <a
                  key={idx}
                  href={btn.href}
                  target={btn.blank ? '_blank' : undefined}
                  rel={btn.blank ? 'noopener noreferrer' : undefined}
                  className="text-left"
                >
                  {Card}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple ways to reach us. Choose what's most convenient for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`text-center rounded-3xl bg-white/70 backdrop-blur shadow-lg border border-gray-100 p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${
                  info.action ? 'cursor-pointer' : ''
                }`}
                onClick={info.action}
              >
                <div className="bg-gradient-to-br from-primary-50 to-blue-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                  <info.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{info.title}</h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                  ))}
                </div>
                {info.action && (
                  <div className="mt-4">
                    <span className="inline-flex items-center gap-1 text-primary-600 hover:underline text-sm font-semibold">
                      Click to {info.title.toLowerCase() === 'phone numbers' ? 'call' : 
                              info.title.toLowerCase() === 'email' ? 'email' : 'view on map'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Tabs and Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Tab Navigation */}
            <div className="flex justify-center mb-10">
              <div className="flex bg-gray-100 rounded-full p-1 shadow-inner border border-gray-200">
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                    activeTab === 'contact'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-primary-600'
                  }`}
                >
                  <MessageSquare className="inline-block mr-2 h-4 w-4" />
                  General Inquiry
                </button>
                <button
                  onClick={() => setActiveTab('appointment')}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                    activeTab === 'appointment'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-primary-600'
                  }`}
                >
                  <Calendar className="inline-block mr-2 h-4 w-4" />
                  Book Appointment
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="rounded-3xl bg-white/85 backdrop-blur border border-gray-100 shadow-2xl p-8 md:p-10 transition-all duration-300 hover:shadow-3xl">
              <div className="text-center mb-8 space-y-2">
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-xs font-semibold">
                  {activeTab === 'contact' ? 'Quick inquiry' : 'Priority appointment'}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {activeTab === 'contact' ? 'Send us a Message' : 'Book an Appointment'}
                </h2>
                <p className="text-gray-600">
                  {activeTab === 'contact'
                    ? 'Have a question or need more information? We\'d love to hear from you.'
                    : 'Schedule your consultation with our orthopedic specialists.'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {activeTab === 'contact' ? 'Subject' : 'Appointment Type'} *
                    </label>
                    {activeTab === 'contact' ? (
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                          placeholder="Brief subject of your inquiry"
                          required
                        />
                      </div>
                    ) : (
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <select
                          name="appointmentType"
                          value={formData.appointmentType}
                          onChange={handleInputChange}
                          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                          required
                        >
                          <option value="">Select Appointment Type</option>
                          {appointmentTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {activeTab === 'appointment' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Date
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="date"
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Time
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <select
                          name="preferredTime"
                          value={formData.preferredTime}
                          onChange={handleInputChange}
                          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                        >
                          <option value="">Select Time Slot</option>
                          {timeSlots.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {activeTab === 'contact' ? 'Message' : 'Additional Information'} *
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
                      placeholder={
                        activeTab === 'contact'
                          ? 'Please describe your inquiry in detail...'
                          : 'Please describe your condition, symptoms, or any specific requirements...'
                      }
                      required
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`inline-flex items-center px-6 py-3 rounded-xl text-white font-semibold shadow-lg transition-all ${
                      isSubmitting
                        ? 'bg-primary-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:shadow-xl hover:-translate-y-0.5'
                    }`}
                  >
                    <Send className="mr-2 h-5 w-5" />
                    {isSubmitting ? 'Sending…' : activeTab === 'contact' ? 'Send Message' : 'Request Appointment'}
                  </button>
                </div>
              </form>

              {activeTab === 'appointment' && (
                <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This is an appointment request. Our team will contact you within 24 hours 
                    to confirm your appointment slot. For urgent matters, please call us directly at +91 77270 48951.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Find Us on Map
            </h2>
            <p className="text-xl text-gray-600">
              Located in Tilak Nagar, Sikar, Rajasthan for easy accessibility.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Map Placeholder */}
            <div className="lg:col-span-2">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100 bg-white">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-100 opacity-80 pointer-events-none" />
                <div className="aspect-video flex items-center justify-center relative">
                  <div className="text-center text-gray-700 space-y-2">
                    <MapPin className="h-14 w-14 mx-auto mb-2 text-primary-600" />
                    <p className="text-lg font-semibold">Interactive Google Map</p>
                    <p className="text-sm">Sharma Ortho Center and Hospital</p>
                    <p className="text-sm">Tilak Nagar, Sikar, Rajasthan 332001</p>
                    <button 
                      onClick={() => window.open('https://maps.google.com/?q=J43W+JV5,+Kuchaman+-+Sikar+-+Didwana+Rd,+Tilak+Nagar,+Sikar,+Rajasthan+332001', '_blank')}
                      className="mt-4 inline-flex items-center justify-center px-5 py-2 rounded-xl bg-primary-600 text-white font-semibold shadow-md hover:shadow-lg transition"
                    >
                      Open in Google Maps
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Directions Info */}
            <div className="space-y-6">
              <div className="rounded-2xl bg-white/80 backdrop-blur border border-gray-100 p-5 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Directions & Landmarks</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  {['Located on Kuchaman - Sikar - Didwana Road', 'Tilak Nagar area, Sikar', 'Easily accessible from main road', 'Ample parking space available'].map((item, idx) => (
                    <div key={idx} className="flex items-start">
                      <span className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-white/80 backdrop-blur border border-gray-100 p-5 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Public Transport</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div>
                    <span className="font-medium text-gray-900">Location:</span>
                    <p>Tilak Nagar, Sikar, Rajasthan</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Bus Routes:</span>
                    <p>Local buses to Sikar available</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Accessibility:</span>
                    <p>Easy access from Sikar city center</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white/80 backdrop-blur border border-gray-100 p-5 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Access</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>📞 Emergency Hotline: +91 77270 48951</p>
                  <p>🚑 24/7 Ambulance Service Available</p>
                  <p>⚡ Direct entry for emergency cases</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about appointments and our services.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: "How do I book an appointment?",
                answer: "You can book an appointment by calling us at +91 77270 48951, using our online form above, or visiting our hospital directly. We also accept WhatsApp appointment requests."
              },
              {
                question: "Do you accept insurance?",
                answer: "Yes, we accept most major health insurance plans including CGHS, ESIC, and private insurance companies. Please bring your insurance card and ID for verification."
              },
              {
                question: "What should I bring for my first visit?",
                answer: "Please bring a valid ID, insurance card (if applicable), list of current medications, previous medical records, and any X-rays or MRI reports related to your condition."
              },
              {
                question: "Do you provide emergency services?",
                answer: "Yes, we provide 24/7 emergency orthopedic services. For emergencies, please call +91 77270 48951 or visit our emergency department directly."
              },
              {
                question: "How long does a typical consultation take?",
                answer: "Initial consultations typically take 30-45 minutes, while follow-up appointments are usually 15-20 minutes. The duration may vary based on the complexity of your condition."
              }
            ].map((faq, index) => (
              <div key={index} className="rounded-2xl border border-gray-100 bg-white shadow-md p-5 hover:shadow-lg transition-all">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
